import React, { useState } from 'react';
import { Play, Plus, Check, Star, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { myListApi, formatDuration, thumbUrl } from '../lib/api';
import { toTitleCase } from '../lib/utils';

export const MovieCard = ({ film, showRank = false, rank = 0 }) => {
  const [inMyList, setInMyList] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleAddToList = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      if (inMyList) {
        await myListApi.remove(film.film_id);
        setInMyList(false);
      } else {
        await myListApi.add(film.film_id);
        setInMyList(true);
      }
    } catch (error) {
      console.error('Failed to update my list:', error);
    }
  };

  const handlePlay = (e) => {
    e.stopPropagation();
    navigate(`/watch/${film.film_id}`);
  };

  // Calculate progress percentage
  const progressPercent = film.watch_progress && film.duration
    ? Math.min((film.watch_progress / film.duration) * 100, 100)
    : 0;
  const hasProgress = progressPercent > 5;
  const isCompleted = progressPercent >= 90;

  // Time remaining
  const timeRemaining = film.watch_progress && film.duration
    ? Math.max(film.duration - film.watch_progress, 0)
    : 0;

  return (
    <div
      className="movie-card relative rounded-sm overflow-hidden cursor-pointer group"
      style={{ width: showRank ? '220px' : '280px', minWidth: showRank ? '220px' : '280px' }}
      onClick={() => navigate(`/film/${film.film_id}`)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={`movie-card-${film.film_id}`}
    >
      {/* Rank Number (for Top 10) */}
      {showRank && rank > 0 && (
        <div className="absolute -left-2 bottom-0 z-20 pointer-events-none" style={{ transform: 'translateX(-30%)' }}>
          <span
            className="text-[120px] font-black leading-none select-none"
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              color: 'transparent',
              WebkitTextStroke: '3px rgba(139, 92, 246, 0.6)',
              textShadow: '0 0 40px rgba(139, 92, 246, 0.3)',
            }}
          >
            {rank}
          </span>
        </div>
      )}

      {/* Thumbnail */}
      <img
        src={thumbUrl(film.thumbnail)}
        alt={film.title}
        className="movie-thumbnail"
        loading="lazy"
      />

      {/* Title Overlay */}
      <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5 pt-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none z-[5] transition-opacity duration-200 group-hover:opacity-0"
        data-testid={`title-overlay-${film.film_id}`}>
        <p className="text-lg md:text-xl font-extrabold text-white line-clamp-2 leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {toTitleCase(film.title)}
        </p>
      </div>

      {/* Premium Content Badge */}
      {film.is_premium_content && (
        <div className="absolute top-2 right-2 z-20 bg-yellow-500/90 text-black px-2 py-1 rounded text-xs font-bold flex items-center gap-1">
          <Lock className="w-3 h-3" />
          ${film.premium_price?.toFixed(2)}
        </div>
      )}

      {/* Watch Progress Bar - Netflix/YouTube style */}
      {hasProgress && (
        <div className="absolute bottom-0 left-0 right-0 z-10" data-testid={`progress-bar-${film.film_id}`}>
          <div className="h-[4px] w-full bg-white/20">
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${progressPercent}%`,
                background: isCompleted
                  ? '#00FF9D'
                  : 'linear-gradient(90deg, #8B5CF6, #a855f7)',
              }}
            />
          </div>
        </div>
      )}

      {/* Hover Overlay */}
      <div className="movie-overlay">
        {/* Quick Actions */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={handlePlay}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-[#00FF9D] transition-colors"
            data-testid={`play-btn-${film.film_id}`}
          >
            <Play className="w-5 h-5 text-black fill-current" />
          </button>
          
          <button
            onClick={handleAddToList}
            className="w-10 h-10 bg-white/20 border border-white/40 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
            data-testid={`mylist-btn-${film.film_id}`}
          >
            {inMyList ? (
              <Check className="w-5 h-5" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Title */}
        <h3 className="font-semibold text-sm line-clamp-2 mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          {toTitleCase(film.title)}
        </h3>

        {/* Metadata */}
        <div className="flex items-center gap-2 text-xs text-gray-300">
          <span className="flex items-center gap-1 text-[#8B5CF6]">
            <Star className="w-3 h-3 fill-current" />
            {film.avg_rating?.toFixed(1) || '0.0'}
          </span>
          <span>{film.year}</span>
          {hasProgress && !isCompleted && timeRemaining > 0 && (
            <>
              <span className="text-gray-500">|</span>
              <span className="text-[#00FF9D]">{formatDuration(timeRemaining)} left</span>
            </>
          )}
          {isCompleted && (
            <>
              <span className="text-gray-500">|</span>
              <span className="text-[#00FF9D] flex items-center gap-1">
                <Check className="w-3 h-3" /> Watched
              </span>
            </>
          )}
        </div>
      </div>

      {/* Glow Border on Hover */}
      {isHovered && (
        <div className="absolute inset-0 ring-2 ring-[#8B5CF6]/50 rounded-sm pointer-events-none" />
      )}
    </div>
  );
};

// Skeleton loader for movie cards
export const MovieCardSkeleton = () => (
  <div
    className="rounded-sm overflow-hidden"
    style={{ width: '280px', minWidth: '280px' }}
  >
    <div className="skeleton aspect-video w-full" />
  </div>
);
